import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Button,
    Chip,
    IconButton,
    Stack,
    Divider,
    Avatar,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Breadcrumbs,
    Link as MuiLink,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Alert,
    Snackbar,
    Autocomplete
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Description as DescriptionIcon,
    Apartment as ApartmentIcon,
    LocationOn as LocationIcon,
    PictureAsPdf as PdfIcon,
    TableView as ExcelIcon,
    ChevronRight,
    Edit as EditIcon,
    Save as SaveIcon,
    Schedule as ScheduleIcon,
    Category as CategoryIcon,
    EventAvailable as EventAvailableIcon,
    EventBusy as EventBusyIcon,
    PrecisionManufacturing as EquipmentIcon,
    Engineering as EngineeringIcon,
    MyLocation as MyLocationIcon,
    Call as CallIcon,
    MailOutline as MailIcon,
    AddCircleOutline as AddIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import apiClient from '../../services/apiClient';
import { formatToIST } from '../../utils/dataUtils';
import { machineLabel } from '../../utils/machineLabel';

/* Status pill colours (centralised — mirror theme palette tokens). */
const STATUS_PILL = {
    ACTIVE:    { dot: '#10b981', bg: '#d1fae5', text: '#059669' },
    DRAFT:     { dot: '#f59e0b', bg: '#fef3c7', text: '#b45309' },
    COMPLETED: { dot: '#94a3b8', bg: '#e2e8f0', text: '#475569' },
    EXPIRED:   { dot: '#ef4444', bg: '#fee2e2', text: '#b91c1c' },
    CANCELLED: { dot: '#ef4444', bg: '#fee2e2', text: '#b91c1c' },
};
const DEFAULT_PILL = { dot: '#2563eb', bg: '#dbeafe', text: '#1d4ed8' };
const pillFor = (status) => STATUS_PILL[String(status || '').toUpperCase()] || DEFAULT_PILL;

const fmtDate = (d) => (d ? formatToIST(d, 'dd MMM yyyy') : null);
const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

const rentalTypeLabel = (t) => {
    const k = String(t || '').toUpperCase();
    if (k === 'MONTHLY') return 'Monthly';
    if (k === 'USAGE_BASED' || k === 'USAGE') return 'Usage based';
    if (k === 'HOURLY') return 'Hourly';
    return t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : 'Standard';
};

/** Human duration between two dates ("12 months" / "18 days"). */
const durationLabel = (start, end) => {
    if (!start || !end) return 'Open-ended';
    const s = new Date(start), e = new Date(end);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || e < s) return '—';
    let months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
    if (e.getDate() < s.getDate()) months -= 1;
    if (months <= 0) {
        const days = Math.max(1, Math.round((e - s) / 86400000));
        return `${days} day${days === 1 ? '' : 's'}`;
    }
    return `${months} month${months === 1 ? '' : 's'}`;
};

/** Build the per-equipment shift chips (S1/S2… + time range). */
const shiftListOf = (device) => {
    const arr = (Array.isArray(device.shifts) && device.shifts.length)
        ? device.shifts
        : [{ start_time: device.shift_start_time, end_time: device.shift_end_time }];
    return arr
        .filter(s => s && (s.start_time || s.end_time))
        .map((s, i) => ({ tag: `S${i + 1}`, time: `${s.start_time || '—'} – ${s.end_time || '—'}` }));
};

/** Status pill — coloured dot + uppercase label. */
const StatusPill = ({ status }) => {
    const s = pillFor(status);
    return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5, borderRadius: '8px', bgcolor: s.bg }}>
            <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: s.dot }} />
            <Typography component="span" sx={{ fontWeight: 800, fontSize: '11px', letterSpacing: '.04em', color: s.text, whiteSpace: 'nowrap' }}>
                {String(status || 'ACTIVE').toUpperCase()}
            </Typography>
        </Box>
    );
};

/** A labelled info tile (icon chip + uppercase label + value). */
const InfoTile = ({ icon, color, label, value }) => (
    <Box display="flex" alignItems="center" gap={1.5} sx={{ minWidth: 0 }}>
        <Avatar variant="rounded" sx={{ width: 40, height: 40, borderRadius: '11px', bgcolor: `${color}18`, color }}>
            {React.cloneElement(icon, { sx: { fontSize: 21 } })}
        </Avatar>
        <Box minWidth={0}>
            <Typography sx={{ fontWeight: 700, fontSize: 10, letterSpacing: '.07em', textTransform: 'uppercase', color: 'text.disabled' }}>{label}</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: 'text.primary', mt: '2px' }}>{value}</Typography>
        </Box>
    </Box>
);

const ContractDetailsPage = () => {
    const { contractId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [contract, setContract] = useState(null);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [updating, setUpdating] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    // Fleet machines for the "add machine by serial number" picker.
    const [machines, setMachines] = useState([]);
    useEffect(() => {
        apiClient.get('/devices')
            .then(res => setMachines((res && res.devices) || res || []))
            .catch(() => { /* picker degrades to a plain equipment-id field */ });
    }, []);
    // equipment_id → serial number (falls back to the id itself when unknown).
    const serialOf = (equipmentId) => {
        const m = machines.find(x => String(x.equipment_id) === String(equipmentId));
        return (m && m.serial_number) || null;
    };

    useEffect(() => {
        if (contractId) {
            fetchContractDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contractId]);

    const fetchContractDetails = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get(`/contracts/${contractId}`);
            if (response.success) {
                setContract(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch contract details:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEditOpen = () => {
        setEditForm({
            ...contract,
            contract_start: contract.contract_start ? contract.contract_start.split('T')[0] : '',
            contract_end: contract.contract_end ? contract.contract_end.split('T')[0] : '',
            contract_signed_date: contract.contract_signed_date ? contract.contract_signed_date.split('T')[0] : '',
            site_location: contract.site_location || '',
            contract_status: contract.contract_status || 'DRAFT',
            rental_type: contract.rental_type || 'MONTHLY',
            class: contract.class || '',
            rental_contract_id: contract.rental_contract_id || '',
            customer_id: contract.customer_id || '',
            hourly_rate: contract.hourly_rate || 0,
            advance_payment: contract.advance_payment || 0,
            monthly_rate: contract.monthly_rate || 0,
            extra_hours: contract.extra_hours || 0,
            equipments: contract.devices ? contract.devices.filter(d => d.status === 'ACTIVE').map(d => ({
                detail_id: d.detail_id,
                sub_contract_id: d.sub_contract_id || '',
                equipment_id: d.equipment_id,
                operator_id: d.operator_id || '',
                shift_start_time: d.shift_start_time || '',
                shift_end_time: d.shift_end_time || '',
                shifts: (Array.isArray(d.shifts) && d.shifts.length)
                    ? d.shifts.map(s => ({ start_time: s.start_time || '', end_time: s.end_time || '' }))
                    : [{ start_time: d.shift_start_time || '', end_time: d.shift_end_time || '' }],
                monthly_rate: d.monthly_rate || 0,
                hourly_rate: d.hourly_rate || 0,
                extra_hours_rate: d.extra_hours_rate || 0,
                advance_payment: d.advance_payment || 0,
                rental_start_date: d.rental_start_date ? d.rental_start_date.split('T')[0] : '',
                rental_end_date: d.rental_end_date ? d.rental_end_date.split('T')[0] : '',
                status: d.status || 'ACTIVE'
            })) : []
        });
        setIsEditOpen(true);
    };

    const handleEditClose = () => {
        setIsEditOpen(false);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setEditForm(prev => ({ ...prev, [name]: value }));
    };

    // ── Multi-shift helpers (each equipment can have N shifts) ──
    const DEFAULT_SHIFTS = [
        { start_time: '08:00', end_time: '18:00' },   // shift 1 — day
        { start_time: '20:00', end_time: '06:00' },   // shift 2 — night
    ];
    const defaultShift = (i) => DEFAULT_SHIFTS[i] || { start_time: '', end_time: '' };

    const syncFirstShift = (eq) => {
        const first = (eq.shifts && eq.shifts[0]) || {};
        eq.shift_start_time = first.start_time || '';
        eq.shift_end_time = first.end_time || '';
        return eq;
    };

    const updateShift = (eqIdx, sIdx, field, value) => {
        setEditForm(prev => {
            const equipments = [...prev.equipments];
            const eq = { ...equipments[eqIdx], shifts: [...(equipments[eqIdx].shifts || [])] };
            eq.shifts[sIdx] = { ...eq.shifts[sIdx], [field]: value };
            equipments[eqIdx] = syncFirstShift(eq);
            return { ...prev, equipments };
        });
    };

    const setShiftCount = (eqIdx, count) => {
        const n = Math.max(1, Math.min(6, parseInt(count, 10) || 1));
        setEditForm(prev => {
            const equipments = [...prev.equipments];
            const cur = equipments[eqIdx].shifts || [];
            const shifts = [];
            for (let i = 0; i < n; i++) shifts.push(cur[i] || defaultShift(i));
            equipments[eqIdx] = syncFirstShift({ ...equipments[eqIdx], shifts });
            return { ...prev, equipments };
        });
    };

    const removeShift = (eqIdx, sIdx) => {
        setEditForm(prev => {
            const equipments = [...prev.equipments];
            const shifts = (equipments[eqIdx].shifts || []).filter((_, i) => i !== sIdx);
            if (!shifts.length) shifts.push(defaultShift(0));
            equipments[eqIdx] = syncFirstShift({ ...equipments[eqIdx], shifts });
            return { ...prev, equipments };
        });
    };

    const handleSave = async () => {
        try {
            setUpdating(true);
            const response = await apiClient.put(`/contracts/${contractId}`, editForm);
            if (response.success) {
                setSnackbar({ open: true, message: 'Contract updated successfully!', severity: 'success' });
                fetchContractDetails();
                handleEditClose();
            } else {
                setSnackbar({ open: true, message: response.message || 'Failed to update contract', severity: 'error' });
            }
        } catch (error) {
            console.error('Update failed:', error);
            const serverMsg =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                error?.message ||
                '';
            let friendly = 'An error occurred while updating.';
            if (/not registered in the fleet/i.test(serverMsg)) {
                friendly = serverMsg;
            } else if (/foreign key constraint/i.test(serverMsg)) {
                friendly = 'One of the equipment IDs you entered is not registered in the fleet. Please add it under Device Management → Device Update → Add New, then try again.';
            } else if (serverMsg) {
                friendly = serverMsg;
            }
            setSnackbar({ open: true, message: friendly, severity: 'error' });
        } finally {
            setUpdating(false);
        }
    };

    const handleSyncExtraHours = async () => {
        try {
            setUpdating(true);
            const response = await apiClient.post('/alerts/sync-extra-hours');
            if (response.success) {
                setSnackbar({ open: true, message: response.message, severity: 'success' });
                fetchContractDetails();
            } else {
                setSnackbar({ open: true, message: 'Sync failed', severity: 'error' });
            }
        } catch (error) {
            console.error('Sync failed:', error);
            setSnackbar({ open: true, message: 'An error occurred during sync', severity: 'error' });
        } finally {
            setUpdating(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Skeleton variant="rounded" height={56} sx={{ mb: 3, maxWidth: 360 }} />
                <Skeleton variant="rounded" height={180} sx={{ mb: 2.5, borderRadius: '16px' }} />
                <Skeleton variant="rounded" height={320} sx={{ borderRadius: '16px' }} />
            </Box>
        );
    }

    if (!contract) {
        return (
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Typography>Contract not found</Typography>
            </Box>
        );
    }

    // One row per machine: a machine can only genuinely be ACTIVE once per
    // contract, so if a stale amend ever leaves two ACTIVE rows for the same
    // equipment (a data glitch, not a real second machine), keep only the most
    // recent one — the count and list must never double-count a machine.
    const activeDevices = Object.values(
        (contract.devices || [])
            .filter(d => d.status === 'ACTIVE')
            .reduce((acc, d) => {
                const key = d.equipment_id || d.sub_contract_id || d.device_id || d.id;
                const prev = acc[key];
                const newer = (a, b) => {
                    const da = new Date(a.effective_from_date || 0).getTime();
                    const db = new Date(b.effective_from_date || 0).getTime();
                    if (da !== db) return da > db ? a : b;
                    return (Number(a.id) || 0) >= (Number(b.id) || 0) ? a : b;
                };
                acc[key] = prev ? newer(prev, d) : d;
                return acc;
            }, {})
    );

    // Main contract details — exactly the fields the rental team needs:
    // contract ID, contractor, site, device count, and the contract window.
    const infoTiles = [
        { label: 'Contract ID', value: contract.rental_contract_id || `#${contract.id}`, icon: <DescriptionIcon />, color: '#2563eb' },
        { label: 'Contractor', value: contract.company_name || '—', icon: <ApartmentIcon />, color: '#7c3aed' },
        { label: 'Site location', value: contract.site_location || '—', icon: <LocationIcon />, color: '#f59e0b' },
        { label: 'No. of devices', value: String(activeDevices.length), icon: <EquipmentIcon />, color: '#0ea5e9' },
        { label: 'Start date', value: fmtDate(contract.contract_start) || '—', icon: <EventAvailableIcon />, color: '#10b981' },
        { label: 'End date', value: fmtDate(contract.contract_end) || 'Open-ended', icon: <EventBusyIcon />, color: '#ef4444' },
    ];

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }} className="animate-fade-in">
            {/* Breadcrumbs */}
            <Breadcrumbs separator={<ChevronRight fontSize="small" />} sx={{ mb: 1.75 }}>
                <MuiLink component="button" onClick={() => navigate('/rentals')} underline="hover" color="primary" sx={{ fontWeight: 600 }}>
                    Rentals
                </MuiLink>
                <Typography color="text.secondary" sx={{ fontWeight: 600 }}>Contract #{contract.id}</Typography>
            </Breadcrumbs>

            {/* Header */}
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'flex-start' }} gap={2} sx={{ mb: 2.75 }}>
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={() => navigate(-1)} aria-label="Back"
                        sx={{ width: 44, height: 44, borderRadius: '13px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', color: 'text.secondary', boxShadow: '0 1px 2px rgb(15 23 42/.05)', '&:hover': { bgcolor: 'background.paper', borderColor: 'primary.main' } }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                            <Typography variant="h4" fontWeight={800}>Contract #{contract.id}</Typography>
                            <StatusPill status={contract.contract_status || 'ACTIVE'} />
                        </Box>
                        <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', fontWeight: 500 }}>
                            {contract.company_name || 'Unassigned'}
                            {contract.rental_contract_id && (
                                <>
                                    <Box component="span" sx={{ color: 'text.disabled', mx: 0.75 }}>·</Box>
                                    <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>{contract.rental_contract_id}</Box>
                                </>
                            )}
                        </Typography>
                    </Box>
                </Box>
                <Stack direction="row" spacing={1.25} alignItems="center" flexShrink={0}>
                    <Button variant="contained" startIcon={<EditIcon />} onClick={handleEditOpen} sx={{ fontWeight: 700 }}>
                        Edit Contract
                    </Button>
                </Stack>
            </Stack>

            {/* Contract details + machine contracts — nothing else */}
            <Stack spacing={2.5} sx={{ minWidth: 0 }}>

                {/* Contract Information */}
                <Card sx={{ borderRadius: '16px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2.75, pt: 2.25 }}>
                        <DescriptionIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                        <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 700 }}>Contract Information</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '14px 30px', px: 2.75, pt: 2.25, pb: 2.75 }}>
                        {infoTiles.map((t) => (
                            <InfoTile key={t.label} icon={t.icon} color={t.color} label={t.label} value={t.value} />
                        ))}
                    </Box>
                </Card>

                {/* Machine contracts — one row per machine on this contract */}
                <Card sx={{ borderRadius: '16px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2.75, py: 2.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <EquipmentIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                        <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 700 }}>Machine Contracts</Typography>
                        <Box component="span" sx={{ fontWeight: 700, fontSize: 11.5, color: 'primary.main', bgcolor: 'rgba(37,99,235,0.08)', px: 1.125, py: 0.25, borderRadius: '7px' }}>
                            {activeDevices.length} machines
                        </Box>
                    </Box>
                    {activeDevices.length > 0 ? (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Machine Contract ID</TableCell>
                                        <TableCell>Machine No.</TableCell>
                                        <TableCell>From</TableCell>
                                        <TableCell>To</TableCell>
                                        <TableCell>Shift</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {activeDevices.map((device, index) => {
                                        const shifts = shiftListOf(device);
                                        return (
                                            <TableRow key={device.detail_id || index} hover>
                                                <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>
                                                    {device.sub_contract_id || '—'}
                                                </TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={1.25}>
                                                        <Avatar variant="rounded" sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: 'rgba(37,99,235,0.08)', color: 'primary.main' }}>
                                                            <EngineeringIcon sx={{ fontSize: 18 }} />
                                                        </Avatar>
                                                        <Box minWidth={0}>
                                                            <Typography variant="body2" fontWeight={700} noWrap>{machineLabel(device, device.device_name || '—')}</Typography>
                                                            {device.variant && <Typography variant="caption" color="text.disabled">{device.variant}</Typography>}
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>
                                                    {fmtDate(device.rental_start_date) || fmtDate(contract.contract_start) || '—'}
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>
                                                    {fmtDate(device.rental_end_date) || 'Ongoing'}
                                                </TableCell>
                                                <TableCell>
                                                    <Stack spacing={0.625} alignItems="flex-start">
                                                        {shifts.length ? shifts.map((sh, i) => (
                                                            <Box key={i} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.875, pl: 0.75, pr: 1.125, py: 0.375, borderRadius: '7px', bgcolor: '#f1f5f9' }}>
                                                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 16, px: 0.5, borderRadius: '5px', bgcolor: 'rgba(37,99,235,0.08)', color: 'primary.main', fontWeight: 800, fontSize: 9 }}>{sh.tag}</Box>
                                                                <Typography component="span" sx={{ fontWeight: 600, fontSize: 11.5, color: 'text.secondary' }}>{sh.time}</Typography>
                                                            </Box>
                                                        )) : <Typography variant="caption" color="text.disabled">—</Typography>}
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Box sx={{ p: 5, textAlign: 'center' }}>
                            <EquipmentIcon sx={{ fontSize: 44, color: 'text.disabled' }} />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 600 }}>No equipment assigned to this contract</Typography>
                        </Box>
                    )}
                </Card>
            </Stack>

            {/* Edit Contract Dialog */}
            <Dialog open={isEditOpen} onClose={handleEditClose} fullWidth maxWidth="md">
                <DialogTitle sx={{ fontWeight: 800, borderBottom: 1, borderColor: 'divider' }}>Edit Contract</DialogTitle>
                <DialogContent sx={{ p: 4 }}>
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth label="Rental Contract ID" name="rental_contract_id" value={editForm.rental_contract_id || ''} onChange={handleFormChange} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth label="Customer ID" name="customer_id" type="number" value={editForm.customer_id || ''} onChange={handleFormChange} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth label="Class" name="class" value={editForm.class || ''} onChange={handleFormChange} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth select label="Status" name="contract_status" value={editForm.contract_status || 'DRAFT'} onChange={handleFormChange}>
                                <MenuItem value="DRAFT">Draft</MenuItem>
                                <MenuItem value="ACTIVE">Active</MenuItem>
                                <MenuItem value="COMPLETED">Completed</MenuItem>
                                <MenuItem value="CANCELLED">Cancelled</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth select label="Rental Type" name="rental_type" value={editForm.rental_type || 'MONTHLY'} onChange={handleFormChange}>
                                <MenuItem value="MONTHLY">Monthly</MenuItem>
                                <MenuItem value="USAGE_BASED">Usage Based</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth type="date" label="Signed Date" name="contract_signed_date" value={editForm.contract_signed_date || ''} onChange={handleFormChange} InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth type="date" label="Start Date" name="contract_start" value={editForm.contract_start || ''} onChange={handleFormChange} InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth type="date" label="End Date" name="contract_end" value={editForm.contract_end || ''} onChange={handleFormChange} InputLabelProps={{ shrink: true }}
                                helperText="Leave blank for an open-ended contract (no end date)" />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField fullWidth type="number" label="Monthly Rate" name="monthly_rate" value={editForm.monthly_rate || 0} onChange={handleFormChange} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField fullWidth type="number" label="Hourly Rate" name="hourly_rate" value={editForm.hourly_rate || 0} onChange={handleFormChange} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField fullWidth type="number" label="Advance Payment" name="advance_payment" value={editForm.advance_payment || 0} onChange={handleFormChange} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField fullWidth type="number" label="Extra Hours" name="extra_hours" value={editForm.extra_hours || 0} onChange={handleFormChange} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Site Location" name="site_location" value={editForm.site_location || ''} onChange={handleFormChange} />
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="subtitle1" fontWeight={800}>Equipment Assignments</Typography>
                                <Button size="small" variant="outlined" startIcon={<AddIcon />}
                                    onClick={() => setEditForm({
                                        ...editForm,
                                        equipments: [...(editForm.equipments || []), {
                                            equipment_id: '',
                                            operator_id: '',
                                            shift_start_time: '08:00',
                                            shift_end_time: '18:00',
                                            shifts: [{ start_time: '08:00', end_time: '18:00' }],
                                            monthly_rate: 0,
                                            hourly_rate: 0,
                                            extra_hours_rate: 0,
                                            advance_payment: 0,
                                            rental_start_date: new Date().toISOString().split('T')[0],
                                            rental_end_date: '',   // open-ended by default; set a date only if the machine has a fixed off-hire
                                            status: 'ACTIVE'
                                        }]
                                    })}>
                                    Add Equipment
                                </Button>
                            </Box>

                            {(editForm.equipments || []).map((eq, idx) => (
                                <Paper key={idx} sx={{ p: 2, mb: 2, bgcolor: eq.detail_id ? '#f0f9ff' : '#f0fdf4', border: `1px dashed ${eq.detail_id ? '#93c5fd' : '#86efac'}`, borderRadius: '12px' }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="body2" fontWeight={800} color={eq.detail_id ? 'primary' : 'success.main'}>
                                            Equipment #{idx + 1} {eq.detail_id ? '(Existing)' : '(New)'}
                                            {eq.sub_contract_id && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>({eq.sub_contract_id})</Typography>}
                                            {['INACTIVE', 'COMPLETED', 'CANCELLED'].includes(String(eq.status || '').toUpperCase()) &&
                                                <Chip label={eq.status} size="small" color="default" sx={{ ml: 1, fontWeight: 800, fontSize: '10px', borderRadius: '6px' }} />}
                                        </Typography>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            {eq.detail_id && String(eq.status || 'ACTIVE').toUpperCase() === 'ACTIVE' && (
                                                <Button size="small" variant="outlined" color="warning" sx={{ fontWeight: 800, borderRadius: '8px' }}
                                                    onClick={() => {
                                                        const newEq = [...editForm.equipments];
                                                        newEq[idx].status = 'INACTIVE';
                                                        if (!newEq[idx].rental_end_date) {
                                                            newEq[idx].rental_end_date = new Date().toISOString().split('T')[0];
                                                        }
                                                        setEditForm({ ...editForm, equipments: newEq });
                                                    }}>
                                                    Close
                                                </Button>
                                            )}
                                            <IconButton size="small" color="error" onClick={() => {
                                                const newEq = [...editForm.equipments];
                                                newEq.splice(idx, 1);
                                                setEditForm({ ...editForm, equipments: newEq });
                                            }}><DeleteIcon fontSize="small" /></IconButton>
                                        </Box>
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                            {eq.detail_id ? (
                                                // Existing line — machine can't be swapped; show its serial number.
                                                <TextField fullWidth size="small" label="Machine (Serial No.)"
                                                    value={serialOf(eq.equipment_id) || eq.equipment_id || ''} disabled
                                                    helperText={serialOf(eq.equipment_id) ? `ID ${eq.equipment_id}` : ' '} />
                                            ) : (
                                                // New line — pick the machine by its serial number; we store its equipment_id.
                                                <Autocomplete
                                                    size="small"
                                                    options={machines}
                                                    getOptionLabel={(m) => m.serial_number
                                                        ? `${m.serial_number}${m.name ? ' · ' + m.name : ''}`
                                                        : (m.equipment_id || '')}
                                                    isOptionEqualToValue={(o, v) => o.equipment_id === v.equipment_id}
                                                    value={machines.find(m => String(m.equipment_id) === String(eq.equipment_id)) || null}
                                                    onChange={(_, m) => {
                                                        const newEq = [...editForm.equipments];
                                                        newEq[idx].equipment_id = m ? m.equipment_id : '';
                                                        setEditForm({ ...editForm, equipments: newEq });
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField {...params} label="Machine (by Serial No.)"
                                                            helperText={eq.equipment_id ? `ID ${eq.equipment_id}` : 'Search by serial number'} />
                                                    )}
                                                />
                                            )}
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField fullWidth size="small" label="Sub Contract ID" value={eq.sub_contract_id || ''} onChange={(e) => {
                                                const newEq = [...editForm.equipments];
                                                newEq[idx].sub_contract_id = e.target.value;
                                                setEditForm({ ...editForm, equipments: newEq });
                                            }} />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField fullWidth size="small" label="Operator ID" type="number" value={eq.operator_id || ''} onChange={(e) => {
                                                const newEq = [...editForm.equipments];
                                                newEq[idx].operator_id = e.target.value;
                                                setEditForm({ ...editForm, equipments: newEq });
                                            }} />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <TextField fullWidth size="small" select label="Equipment Status" value={eq.status || 'ACTIVE'} onChange={(e) => {
                                                const newEq = [...editForm.equipments];
                                                newEq[idx].status = e.target.value;
                                                setEditForm({ ...editForm, equipments: newEq });
                                            }}>
                                                <MenuItem value="ACTIVE">Active</MenuItem>
                                                <MenuItem value="INACTIVE">Inactive (Closed)</MenuItem>
                                                <MenuItem value="COMPLETED">Completed</MenuItem>
                                                <MenuItem value="CANCELLED">Cancelled</MenuItem>
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: 'rgba(0,0,0,0.02)', border: '1px solid', borderColor: 'divider' }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                                    <Typography variant="caption" fontWeight={800} color="text.secondary">SHIFTS</Typography>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Typography variant="caption" color="text.secondary"># of shifts</Typography>
                                                        <TextField size="small" type="number" inputProps={{ min: 1, max: 6, style: { width: 56, textAlign: 'center' } }}
                                                            value={(eq.shifts || []).length || 1}
                                                            onChange={(e) => setShiftCount(idx, e.target.value)} />
                                                        <Button size="small" startIcon={<AddIcon />} onClick={() => setShiftCount(idx, (eq.shifts || []).length + 1)} sx={{ fontWeight: 700 }}>Add shift</Button>
                                                    </Box>
                                                </Box>
                                                {(eq.shifts && eq.shifts.length ? eq.shifts : [{ start_time: '', end_time: '' }]).map((sh, sIdx) => (
                                                    <Box key={sIdx} display="flex" alignItems="center" gap={1.5} mb={1}>
                                                        <Chip label={`Shift ${sIdx + 1}`} size="small" sx={{ fontWeight: 700, minWidth: 70 }} />
                                                        <TextField size="small" type="time" label="Start" InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}
                                                            value={sh.start_time || ''} onChange={(e) => updateShift(idx, sIdx, 'start_time', e.target.value)} />
                                                        <TextField size="small" type="time" label="End" InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}
                                                            value={sh.end_time || ''} onChange={(e) => updateShift(idx, sIdx, 'end_time', e.target.value)} />
                                                        <IconButton size="small" color="error" disabled={(eq.shifts || []).length <= 1}
                                                            onClick={() => removeShift(idx, sIdx)}><DeleteIcon fontSize="small" /></IconButton>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <TextField fullWidth size="small" type="number" label="Monthly Rate" value={eq.monthly_rate || 0} onChange={(e) => {
                                                const newEq = [...editForm.equipments];
                                                newEq[idx].monthly_rate = e.target.value;
                                                setEditForm({ ...editForm, equipments: newEq });
                                            }} />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <TextField fullWidth size="small" type="number" label="Hourly Rate" value={eq.hourly_rate || 0} onChange={(e) => {
                                                const newEq = [...editForm.equipments];
                                                newEq[idx].hourly_rate = e.target.value;
                                                setEditForm({ ...editForm, equipments: newEq });
                                            }} />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <TextField fullWidth size="small" type="number" label="Extra Hours Rate" value={eq.extra_hours_rate || 0} onChange={(e) => {
                                                const newEq = [...editForm.equipments];
                                                newEq[idx].extra_hours_rate = e.target.value;
                                                setEditForm({ ...editForm, equipments: newEq });
                                            }} />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <TextField fullWidth size="small" type="number" label="Advance Payment" value={eq.advance_payment || 0} onChange={(e) => {
                                                const newEq = [...editForm.equipments];
                                                newEq[idx].advance_payment = e.target.value;
                                                setEditForm({ ...editForm, equipments: newEq });
                                            }} />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField fullWidth size="small" type="date" label="Effective From" InputLabelProps={{ shrink: true }} value={eq.rental_start_date || ''} onChange={(e) => {
                                                const newEq = [...editForm.equipments];
                                                newEq[idx].rental_start_date = e.target.value;
                                                setEditForm({ ...editForm, equipments: newEq });
                                            }} />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField fullWidth size="small" type="date" label="Effective To" InputLabelProps={{ shrink: true }} value={eq.rental_end_date || ''} onChange={(e) => {
                                                const newEq = [...editForm.equipments];
                                                newEq[idx].rental_end_date = e.target.value;
                                                setEditForm({ ...editForm, equipments: newEq });
                                            }} helperText="Blank = open-ended (no off-hire date)" />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            ))}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
                    <Button onClick={handleEditClose} color="inherit" sx={{ fontWeight: 700 }}>Cancel</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={updating}
                        startIcon={updating ? <Skeleton variant="circular" width={20} height={20} /> : <SaveIcon />}
                        sx={{ fontWeight: 800 }}
                    >
                        {updating ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Notification Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%', fontWeight: 600 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ContractDetailsPage;
